import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

@Component({
  selector: 'app-import-workflow-edit',
  templateUrl: './import-workflow-edit.component.html',
  styleUrls: ['./import-workflow-edit.component.scss'],
})
export class ImportWorkflowEditComponent implements OnInit {
  form!: FormGroup;
  template: any;
  priorityData: any[] = [];
  statusData: any[] = [];
  memberList: any[] = [];
  followerList: any[] = [];
  isTimeSheetSetting = false;
  mainTasksStatusFormCtr = new FormControl({ value: null, disabled: true });
  statusFormCtr = new FormControl({ value: null, disabled: true });
  submitBtnDisable = false;

  private originalTasks: any[] = [];
  private membersById = new Map<number, any>();

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<ImportWorkflowEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.dialogRef.disableClose = true;
    this.template = data?.template || {};
    this.priorityData = data?.priorityData || [];
    this.statusData = data?.statusData || [];
    this.memberList = data?.memberList || [];
    this.followerList = data?.followerList || [];
    this.isTimeSheetSetting = !!data?.isTimeSheetSetting;
    const mergedMemberList = [
      ...(this.memberList || []),
      ...(this.followerList || []),
    ];
    this.membersById = new Map(
      mergedMemberList.map((member: any) => [Number(member.id), member])
    );
  }

  ngOnInit(): void {
    this.originalTasks = JSON.parse(JSON.stringify(this.template?.tasks || []));
    this.statusData = (this.statusData || [])
      .map((status: any) => this.normalizeStatusOption(status))
      .filter((status: any) => !!status?.id);
    const mainTaskStatusSource = this.buildStatusSource(
      this.template?.main_task_status ?? this.template?.workflow_data?.main_task_status,
      this.template?.main_task_status_name ?? this.template?.workflow_data?.main_task_status_name
    );
    const taskToTaskStatusSource = this.buildStatusSource(
      this.template?.status ?? this.template?.workflow_data?.status,
      this.template?.status_name ?? this.template?.workflow_data?.status_name
    );
    this.ensureSelectedStatusVisible(mainTaskStatusSource, this.mainTasksStatusFormCtr);
    this.ensureSelectedStatusVisible(taskToTaskStatusSource, this.statusFormCtr);
    this.form = this.fb.group({
      tasks: this.fb.array(
        this.originalTasks.map((task: any) => this.createSubTask(task))
      ),
    });
  }

  get tasks(): FormArray {
    return this.form.get('tasks') as FormArray;
  }

  get subTasks(): FormArray {
    return this.form.get('tasks') as FormArray;
  }

  get templateTypeLabel(): string {
    switch ((this.template?.template_type || '').toLowerCase()) {
      case 'task_to_subtask':
        return 'Task to Subtask';
      case 'task_to_task':
        return 'Task to Task';
      default:
        return this.template?.template_type || '-';
    }
  }

  get subTypeLabel(): string {
    switch ((this.template?.sub_type || '').toLowerCase()) {
      case 'on_status':
        return 'On Status';
      case 'on_day_gap':
        return 'On Day Gap';
      default:
        return this.template?.sub_type || '-';
    }
  }

  get selectedStatusLabel(): string {
    return (
      this.statusFormCtr?.value?.status_name ||
      this.template?.status?.status_name ||
      this.template?.workflow_data?.status?.status_name ||
      '-'
    );
  }

  get isWorkflowEditFormValid(): boolean {
    if (!this.isStatusTriggerType) {
      return this.form?.valid;
    }
    return (
      this.form?.valid &&
      !!this.mainTasksStatusFormCtr?.value &&
      !!this.statusFormCtr?.value
    );
  }

  get isStatusTriggerType(): boolean {
    return (this.template?.sub_type || '').toLowerCase() === 'on_status';
  }

  private resolveStatusValue(rawStatus: any): any {
    if (!rawStatus) return null;
    const statusId = Number(rawStatus?.id ?? rawStatus?.status_id ?? rawStatus);
    if (!Number.isNaN(statusId)) {
      const matchedById =
        this.statusData.find(
          (status: any) => Number(status?.id ?? status?.status_id) === statusId
        ) ||
        null;
      if (matchedById) return matchedById;
    }

    const statusName = String(
      rawStatus?.status_name ?? rawStatus?.name ?? rawStatus
    ).trim().toLowerCase();
    if (statusName && statusName !== 'undefined' && statusName !== 'null') {
      const matchedByName =
        this.statusData.find(
          (status: any) =>
            String(status?.status_name || '')
              .trim()
              .toLowerCase() === statusName
        ) || null;
      if (matchedByName) return matchedByName;
    }

    const statusNameOnly = String(
      rawStatus?.status_name ?? rawStatus?.name ?? ''
    ).trim();
    if (statusNameOnly) {
      return {
        id: rawStatus?.id ?? rawStatus?.status_id ?? null,
        status_name: statusNameOnly,
        status_color: rawStatus?.status_color || '#363F4D',
      };
    }

    if (typeof rawStatus === 'object') {
      return {
        id: rawStatus?.id ?? rawStatus?.status_id ?? null,
        status_name: rawStatus?.status_name || rawStatus?.name || '',
        status_color: rawStatus?.status_color || '#363F4D',
      };
    }

    return null;
  }

  private normalizeStatusOption(status: any): any {
    if (!status) return null;
    const id = status?.id ?? status?.status_id ?? null;
    const statusName = status?.status_name ?? status?.name ?? '';
    return {
      ...status,
      id,
      status_name: statusName,
      status_color: status?.status_color || '#363F4D',
    };
  }

  private buildStatusSource(rawStatus: any, fallbackName: any): any {
    if (rawStatus && typeof rawStatus === 'object') {
      return {
        ...rawStatus,
        status_name: rawStatus?.status_name || rawStatus?.name || fallbackName || '',
      };
    }
    if (rawStatus !== null && rawStatus !== undefined) {
      return {
        id: rawStatus,
        status_name: fallbackName || '',
      };
    }
    if (fallbackName) {
      return { status_name: fallbackName };
    }
    return null;
  }

  private sameStatus(a: any, b: any): boolean {
    const aId = Number(a?.id);
    const bId = Number(b?.id);
    if (!Number.isNaN(aId) && !Number.isNaN(bId)) return aId === bId;
    const aName = String(a?.status_name || '').trim().toLowerCase();
    const bName = String(b?.status_name || '').trim().toLowerCase();
    return !!aName && !!bName && aName === bName;
  }

  private ensureSelectedStatusVisible(rawStatus: any, control: FormControl): void {
    const resolved = this.normalizeStatusOption(this.resolveStatusValue(rawStatus));
    if (!resolved) {
      control.setValue(null);
      return;
    }
    const exists = (this.statusData || []).some((status: any) =>
      this.sameStatus(status, resolved)
    );
    if (!exists) {
      this.statusData = [...(this.statusData || []), resolved];
    }
    control.setValue(resolved);
  }

  private createSubTask(task: any): FormGroup {
    const formGroup = this.fb.group(
      {
        step_id: [task?._id || ''],
        subtask_name: [task?.subtask_name || '', Validators.required],
        priority: [task?.priority || null, Validators.required],
        assignees: [task?.assignees || []],
        groups: [task?.groups || []],
        follower: [task?.follower || []],
        start_date: [task?.start_date ?? null],
        due_date: [
          task?.due_date ?? null,
          [Validators.required, Validators.min(0), Validators.max(30)],
        ],
        day_gap: [task?.day_gap ?? null],
      },
      {
        validators: [this.assigneeOrGroupRequiredValidator],
      }
    );

    if (this.isTimeSheetSetting) {
      formGroup.get('start_date')?.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(30),
      ]);
      formGroup.setValidators([
        this.assigneeOrGroupRequiredValidator,
        this.dueDateAfterStartDateValidator,
      ]);
    }

    if (this.template?.sub_type === 'on_day_gap') {
      formGroup.get('day_gap')?.setValidators([
        Validators.required,
        Validators.min(0),
        Validators.max(30),
      ]);
    }

    return formGroup;
  }

  assigneeOrGroupRequiredValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const assignees = control.get('assignees')?.value || [];
    const groups = control.get('groups')?.value || [];
    const hasAssignees = Array.isArray(assignees) && assignees.length > 0;
    const hasGroups = Array.isArray(groups) && groups.length > 0;
    return hasAssignees || hasGroups ? null : { assigneeOrGroupRequired: true };
  };

  dueDateAfterStartDateValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const startDate = control.get('start_date')?.value;
    const dueDate = control.get('due_date')?.value;
    const hasStartDate =
      startDate !== null && startDate !== undefined && startDate !== '';
    const hasDueDate =
      dueDate !== null && dueDate !== undefined && dueDate !== '';

    if (hasStartDate && hasDueDate && Number(dueDate) < Number(startDate)) {
      control.get('start_date')?.setErrors({ dueDateBeforeStart: true });
      return { dueDateBeforeStart: true };
    }

    if (control.get('start_date')?.hasError('dueDateBeforeStart')) {
      control.get('start_date')?.setErrors(null);
    }
    return null;
  };

  selectedId(event: any, index: number): void {
    const group: any =
      event?.task_member?.filter((ele: any) => ele.group === 'group') || [];
    const member: any =
      event?.task_member?.filter((ele: any) => ele.group === 'member') || [];
    const follower: any =
      event?.task_follower?.filter((ele: any) => ele.group === 'member') || [];

    const row = this.subTasks.at(index) as FormGroup;
    row.get('follower')?.setValue(
      follower.map((data: any) => ({
        first_name: data?.first_name || '',
        id: data.id,
        last_name: data?.last_name || '',
        profile_image: data?.profile_image || '',
      }))
    );

    if (member?.length) {
      row.get('assignees')?.setValue(
        member.map((data: any) => ({
          first_name: data?.first_name || '',
          id: data.id,
          last_name: data?.last_name || '',
          profile_image: data?.profile_image || '',
        }))
      );
      row.get('groups')?.setValue([]);
    } else if (group?.length) {
      row.get('groups')?.setValue(
        group.map((data: any) => ({
          name: data?.itemName || data?.name || '',
          id: data.id,
          profile_image: data?.profile_image || '',
        }))
      );
      row.get('assignees')?.setValue([]);
    } else {
      row.get('groups')?.setValue([]);
      row.get('assignees')?.setValue([]);
    }
    row.updateValueAndValidity();
    this.form.markAsDirty();
  }

  close(): void {
    this.dialogRef.close();
  }

  private toMemberObject(id: number, fallbackMembers: any[] = []): any {
    const idNum = Number(id);
    const fromPicker = this.membersById.get(idNum);
    if (fromPicker) {
      return {
        id: idNum,
        first_name: fromPicker.first_name || '',
        last_name: fromPicker.last_name || '',
        profile_image: fromPicker.profile_image || '',
      };
    }

    const fallback = fallbackMembers.find((m: any) => Number(m?.id) === idNum);
    return fallback || { id: idNum, first_name: '', last_name: '', profile_image: '' };
  }

  private arraysEqual(a: any[], b: any[]): boolean {
    const first = [...(a || [])].sort();
    const second = [...(b || [])].sort();
    return JSON.stringify(first) === JSON.stringify(second);
  }

  private toNullableNumber(value: any): number | null {
    if (value === '' || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  saveTemplate(): void {
    if (this.submitBtnDisable) return;
    if (!this.isWorkflowEditFormValid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitBtnDisable = true;
    const formTasks = this.subTasks.value || [];
    const taskOverrides: any[] = [];

    for (let i = 0; i < formTasks.length; i++) {
      const current = formTasks[i] || {};
      const original = this.originalTasks[i] || {};
      const override: any = {
        index: i,
      };

      if ((current.subtask_name || '') !== (original.subtask_name || '')) {
        override.subtask_name = current.subtask_name || '';
      }

      const originalPriority = JSON.stringify(original.priority || null);
      const currentPriority = JSON.stringify(current.priority || null);
      if (currentPriority !== originalPriority) {
        override.priority = current.priority || null;
      }

      const originalAssigneeIds = (original.assignees || []).map((a: any) => Number(a?.id));
      const currentAssigneeIds = (current.assignees || []).map((a: any) => Number(a?.id));
      if (!this.arraysEqual(currentAssigneeIds, originalAssigneeIds)) {
        override.assignees = (current.assignees || []).map((member: any) =>
          this.toMemberObject(member?.id, original.assignees || [])
        );
      }

      const originalFollowerIds = (original.follower || []).map((f: any) => Number(f?.id));
      const currentFollowerIds = (current.follower || []).map((f: any) => Number(f?.id));
      if (!this.arraysEqual(currentFollowerIds, originalFollowerIds)) {
        override.follower = (current.follower || []).map((member: any) =>
          this.toMemberObject(member?.id, original.follower || [])
        );
      }

      const originalGroupIds = (original.groups || []).map((group: any) => Number(group?.id));
      const currentGroupIds = (current.groups || []).map((group: any) => Number(group?.id));
      if (!this.arraysEqual(currentGroupIds, originalGroupIds)) {
        override.groups = current.groups || [];
      }

      const currentStartDate = this.toNullableNumber(current.start_date);
      const originalStartDate = this.toNullableNumber(original.start_date);
      if (currentStartDate !== originalStartDate) {
        override.start_date = currentStartDate;
      }

      const currentDueDate = this.toNullableNumber(current.due_date);
      const originalDueDate = this.toNullableNumber(original.due_date);
      if (currentDueDate !== originalDueDate) {
        override.due_date = currentDueDate;
      }

      const currentDayGap = this.toNullableNumber(current.day_gap);
      const originalDayGap = this.toNullableNumber(original.day_gap);
      if (currentDayGap !== originalDayGap) {
        override.day_gap = currentDayGap;
      }

      if (Object.keys(override).length > 1) {
        taskOverrides.push(override);
      }
    }

    const closePayload: any = {
      edited_template_preview: {
        ...this.template,
        tasks: formTasks,
      },
    };

    if (taskOverrides.length > 0) {
      closePayload.workflow_overrides = {
        task_overrides: taskOverrides,
      };
    }

    this.dialogRef.close(closePayload);
  }
}
